import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { Penalty, PenaltyCreate, PenaltyUpdate, QueryParams } from "../types";

const PenaltyManagement: React.FC = () => {
  const [penalties, setPenalties] = useState<Penalty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPenalty, setSelectedPenalty] = useState<Penalty | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<PenaltyCreate>({
    code: "",
    description: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch penalties data
  const fetchPenalties = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getPenalties(params);
      setPenalties(response.data);
    } catch (error: any) {
      setError("Failed to fetch penalties");
      console.error("Error fetching penalties:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPenalties();
  }, [searchTerm, includeDisabled]);

  const handleOpenModal = (penalty?: Penalty) => {
    if (penalty) {
      setSelectedPenalty(penalty);
      setFormData({
        code: penalty.code,
        description: penalty.description || "",
      });
      setIsEditing(true);
    } else {
      setSelectedPenalty(null);
      setFormData({
        code: "",
        description: "",
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.code) {
      toast({
        title: "Validation Error",
        description: "Code is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEditing && selectedPenalty) {
        const updateData: PenaltyUpdate = {
          code: formData.code,
          description: formData.description || undefined,
        };
        await adminApiService.updatePenalty(selectedPenalty.id, updateData);
        toast({
          title: "Success",
          description: "Penalty updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createPenalty(formData);
        toast({
          title: "Success",
          description: "Penalty created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchPenalties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} penalty: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting penalty:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (penalty: Penalty) => {
    try {
      await adminApiService.deletePenalty(penalty.id);
      toast({
        title: "Success",
        description: "Penalty deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchPenalties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete penalty: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting penalty:", error);
    }
  };

  const handleRestore = async (penalty: Penalty) => {
    try {
      await adminApiService.restorePenalty(penalty.id);
      toast({
        title: "Success",
        description: "Penalty restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchPenalties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore penalty: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring penalty:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading penalties...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Penalty Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search penalties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Penalty
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Penalty Code</Th>
                <Th>Description</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {penalties.map((penalty) => (
                <Tr key={penalty.id}>
                  <Td>
                    <Badge colorScheme="red" variant="solid">
                      {penalty.code}
                    </Badge>
                  </Td>
                  <Td>
                    <Text noOfLines={2} maxW="300px">
                      {penalty.description || "-"}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={penalty.is_disabled ? "red" : "green"}>
                      {penalty.is_disabled ? "Disabled" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(penalty)}
                      />
                      {!penalty.is_disabled ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(penalty)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(penalty)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(penalty)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {penalties.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No penalties found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Penalty Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Penalty" : "Add New Penalty"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Penalty Code</FormLabel>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="Enter penalty code"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter description (optional)"
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PenaltyManagement;
